import React, { useCallback, useMemo, useState } from "react";
import { useFragment } from "react-relay";

import cx from "classnames";
import { noop } from "lodash";
import moment from "moment";
import { graphql } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Avatar, Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import type { ModalProps } from "~/ui/modals";
import toasty from "~/ui/toast";

import { ChaptersPanel } from "./ChaptersPanel";
import { ActionItemsHighlight, DecisionsHighlight, SummaryHighlight } from "./HighlightsPanel";

import type {
	RecapHistory_meetingRecording$key,
	RecapHistory_meetingRecording$data as RecapHistoryMeetingRecording,
} from "~/__generated__/RecapHistory_meetingRecording.graphql";
import type { RecapHistoryChangeSummaryMutation } from "~/__generated__/RecapHistoryChangeSummaryMutation.graphql";

import styles from "./css/RecapHistory.module.css";

const recapHistoryFragment = graphql`
	fragment RecapHistory_meetingRecording on MeetingRecording {
		id
		meetingRecordedVideoDuration
		activeSummary {
			id
		}
		summaries {
			id
			summary
			createdAt
			actionItems {
				timestamp
				text
				assignees {
					userId
					fullName
				}
				insertedBlockKey
				edited
			}
			chapters {
				timestamp
				title
				bulletPoints {
					text
					timestamp
					textShort
					context
				}
			}
			decisions {
				...HighlightsPanel_DecisionsHighlight_decisions
			}
			creator {
				id
				fullName
				email
				image
			}
		}
	}
`;

const changeSummaryMutation = graphql`
	mutation RecapHistoryChangeSummaryMutation($meetingRecordingId: ID!, $meetingRecordingSummaryId: ID!) {
		changeSummary(meetingRecordingId: $meetingRecordingId, meetingRecordingSummaryId: $meetingRecordingSummaryId) {
			recording {
				id
				activeSummary {
					id
				}
				...TranscriptModal_meetingRecording @arguments(public: false)
			}
		}
	}
`;

type Summary = NonNullable<NonNullable<RecapHistoryMeetingRecording["summaries"]>[0]>;

interface SummaryPanelItemProps {
	summary: Summary;
	onSelect(summary: Summary): void;
	isCurrent?: boolean;
	isSelected?: boolean;
}

function SummaryPanelItem({ summary, onSelect, isCurrent, isSelected }: SummaryPanelItemProps) {
	const onClick = useCallback(() => onSelect(summary), [onSelect, summary]);

	return (
		<div className={styles.summaryPanelItem}>
			{isCurrent && <small className={styles.version}>current version</small>}
			<Button className={cx(styles.summaryPanelItemButton, { [styles.selected]: isSelected })} onClick={onClick}>
				{moment(summary.createdAt).format("MMM DD, YYYY [at] HH:mm A")}
				{summary.creator && (
					<div className={styles.avatar}>
						<Avatar user={summary.creator} className={styles.avatarImage} />
						<small>{summary.creator.fullName}</small>
					</div>
				)}
			</Button>
		</div>
	);
}

interface RecapHistoryProps {
	meetingRecording: RecapHistory_meetingRecording$key;
}

export default function RecapHistory({
	meetingRecording: meetingRecordingKey,
	close,
}: ModalProps<null> & RecapHistoryProps) {
	const meetingRecording = useFragment(recapHistoryFragment, meetingRecordingKey);
	const onClose = useCallback(() => {
		close();
	}, [close]);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null);

	const [activeSummary, otherSummaries] = useMemo(() => {
		const summaries = meetingRecording.summaries;
		const activeSummary = summaries?.find(summary => summary.id === meetingRecording.activeSummary?.id);
		if (!selectedSummary && activeSummary) {
			setSelectedSummary(activeSummary);
		}
		const otherSummaries = summaries?.filter(summary => summary.id !== meetingRecording.activeSummary?.id);
		return [activeSummary, otherSummaries];
	}, [selectedSummary, meetingRecording]);

	const onChangeSummary = useCallback(() => {
		if (!selectedSummary || activeSummary?.id === selectedSummary.id) return;
		setIsLoading(true);
		commitMutationPromise<RecapHistoryChangeSummaryMutation>(environment, {
			mutation: changeSummaryMutation,
			variables: {
				meetingRecordingId: meetingRecording.id,
				meetingRecordingSummaryId: selectedSummary.id,
			},
			onCompleted: () => {
				setIsLoading(false);
				onClose();
			},
			onError: () => {
				toasty.error("Failed to change summary");
				setIsLoading(false);
				onClose();
			},
		});
	}, [meetingRecording, selectedSummary, activeSummary, onClose]);

	return (
		<Modal onClose={onClose} className={styles.modal}>
			<Modal.Header onClose={onClose} className={styles.header}>
				Version History
			</Modal.Header>
			<Modal.Body className={styles.content}>
				<div className={styles.leftPanel}>
					{activeSummary && (
						<SummaryPanelItem
							summary={activeSummary}
							isCurrent={true}
							isSelected={selectedSummary?.id === activeSummary.id}
							onSelect={setSelectedSummary}
						/>
					)}
					{otherSummaries && (
						<div className={styles.otherSummaries}>
							{otherSummaries.map(summary => (
								<SummaryPanelItem
									key={summary.id}
									summary={summary}
									isCurrent={false}
									isSelected={selectedSummary?.id === summary.id}
									onSelect={setSelectedSummary}
								/>
							))}
						</div>
					)}
				</div>
				{selectedSummary && (
					<>
						<div className={styles.data}>
							<SummaryHighlight
								mode="readonly"
								summary={selectedSummary.summary ?? ""}
								recordingDuration={meetingRecording.meetingRecordedVideoDuration}
							/>
							<ChaptersPanel
								mode="readonly"
								collapsible
								recordingId={meetingRecording.id}
								chapters={selectedSummary.chapters}
								userVideoProgress={0}
								onTimestampClick={noop}
								onDislike={noop}
								recordingTitle=""
								videoDuration={0}
								thumbnailTrack={null}
								noVideo
							/>
							<ActionItemsHighlight
								readOnly
								noVideo
								actionItems={selectedSummary.actionItems}
								recordingId={meetingRecording.id}
								noteId={""}
								videoDuration={0}
								// TODO: ENG-188
								onTimestampClick={noop}
							/>
							<DecisionsHighlight
								mode="readonly"
								noVideo
								decisionsKey={selectedSummary.decisions}
								recordingId={meetingRecording.id}
								videoDuration={0}
							/>
						</div>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button
					size="normal"
					variant="primary"
					onClick={onChangeSummary}
					disabled={selectedSummary?.id === activeSummary?.id || isLoading}
				>
					{selectedSummary?.id === activeSummary?.id ? (
						"Current version"
					) : isLoading ? (
						<LoadingSpinner inline dimmer={false} size="xs" />
					) : (
						"Restore this version"
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
